import { baseVariables, toRem } from '@marriott/mi-ui-library';
import styled from 'styled-components';
import { chatKeyboardFocusVisible } from '../../styles/chatFocus';

interface WrapperProps {
  expanded: boolean;
  alignment?: 'bottom' | 'top';
  $enableHoverActions?: boolean;
}

export const Wrapper = styled.div<WrapperProps>`
  flex-direction: column;
  height: 100%;
  transition: width 0.7s ease;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.07);
  background: ${baseVariables.color['base20']};
  align-items: center;
  gap: ${toRem(32)};
  flex-shrink: 0;
  padding-bottom: ${toRem(4)};
  overflow: hidden;

  .new-chat-item {
    width: 100%;
    display: flex;
    justify-content: center;
    flex-shrink: 0;
  }
  .settings-item {
    width: inherit;
    display: flex;
    justify-content: center;
    z-index: 1;
    flex-shrink: 0;
  }

  /* Mobile defaults (< 768px) */
  display: ${({ expanded }) => (expanded ? 'flex' : 'none')};
  width: 90%;
  height: 100vh;
  margin-right: ${({ expanded }) => (expanded ? toRem(1) : toRem(0))};
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  .new-chat-item {
    margin: ${toRem(32)} 0;
  }
  .settings-item {
    position: fixed;
    background-color: ${baseVariables.color['base20']};
    height: ${toRem(48)};
    width: calc(90% - ${toRem(25)});
    bottom: ${toRem(0)} !important;
    padding-right: 24px;
    button {
      align-items: center;
      margin-top: ${toRem(0)};
    }
  }

  /* Tablet/Desktop (768px and up) */
  @media ${baseVariables.mediaQuery.lg} {
    display: flex !important;
    width: ${({ expanded }) => (expanded ? toRem(250) : toRem(81))};
    margin-right: ${toRem(1)};
    position: inherit;
    z-index: auto;

    .new-chat-item {
      margin: 0;
    }
    .settings-item {
      width: inherit;
      bottom: ${toRem(24)};
      position: fixed;
      padding-left: ${({ expanded }) => (expanded ? toRem(16) : 0)};
    }
  }

  .toggle-button {
    display: none;
    transition: opacity 0.9s ease;
  }
`;

export const ToggleButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: var(--t-brand-fg-color, #1c1c1c);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  flex-shrink: 0;
  background: var(--t-icon-btn-primary-bg-color, #eeeeee);
  border-radius: 4px;

  &:hover {
    background: black;
    opacity: 0.7;
    transition: opacity 0.9s ease;
    .icon {
      color: white !important;
    }
  }

  ${chatKeyboardFocusVisible}
`;

export const LogoWrapper = styled.div<WrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ expanded }) => (expanded ? 'space-between' : 'center')};
  width: 100%;
  svg {
    cursor: pointer;
  }

  /* Collapsed: sparkle only; chevron overlays same slot on hover/focus (WEB-193594 keeps expand in tab order) */
  ${({ $enableHoverActions, expanded }) =>
    $enableHoverActions &&
    !expanded &&
    `
    position: relative;
    width: auto;
    min-width: ${toRem(32)};
    min-height: ${toRem(32)};

    .toggle-button {
      position: absolute;
      inset: 0;
      margin: auto;
      display: flex;
      opacity: 0;
      transition: opacity 0.5s ease;
    }

    .logo-container {
      transition: opacity 0.5s ease;
    }

    &:hover,
    &:focus-within {
      .logo-container {
        opacity: 0;
      }

      .toggle-button {
        opacity: 1;
      }
    }
  `}
`;

export const ChatIconButton = styled.button<WrapperProps>`
  width: 100%;
  background: none;
  border-radius: ${toRem(4)};
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: ${({ expanded }) => (expanded ? 'flex-start' : 'center')};
  padding: ${({ expanded }) => expanded && `0 ${toRem(16)}`};
  color: var(--t-brand-fg-color, #1c1c1c);
  gap: ${({ expanded }) => (expanded ? toRem(8) : 0)};
  font-family: ${baseVariables.font.fontFamilySwiss};
  transition: width 0.3s ease;
  &:hover {
    background: ${({ expanded }) => (expanded ? 'none' : '#f5f5f5')};
  }
`;

export const NewChatText = styled.div`
  white-space: nowrap;
  font-size: ${toRem(14)} !important;
`;

export const ChatHistoryHeading = styled.h2<WrapperProps>`
  white-space: nowrap;
  margin: 0;
  font: inherit;
`;

export const ChatList = styled.ul<WrapperProps>`
  list-style: none;
  padding: 0;
  margin: 0;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  display: ${({ expanded }) => (expanded ? 'flex' : 'none')};
  flex: 1;
  min-height: 0;
  overflow-y: visible;
  overflow-x: hidden;
  li {
    width: 100%;
    &:hover {
      .chat-actions {
        visibility: visible !important;
        pointer-events: auto !important;
      }
    }
  }
`;

export const ChatHistoryWrapper = styled.div`
  width: 100%;
  gap: ${toRem(12)};
  flex: 1;
  min-height: 0;
  max-height: calc(100vh - ${toRem(250)});
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: ${toRem(100)};

  @media ${baseVariables.mediaQuery.lg} {
    max-height: calc(80vh - ${toRem(180)});
    padding-bottom: ${toRem(80)};
  }
`;

export const ChatActions = styled.div`
  display: none;
`;

export const LogoContainer = styled.button<{ $isInteractive?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  overflow: hidden;
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'pointer' : 'default')};
  &:disabled {
    cursor: default;
  }

  ${chatKeyboardFocusVisible}

  @media ${baseVariables.mediaQuery.tablet} {
    overflow: inherit;
  }
`;

export const BrandNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${toRem(4)};
  margin-left: ${toRem(8)};
`;

export const BrandName = styled.span`
  white-space: nowrap;
  color: var(--t-brand-fg-color, #1c1c1c);
`;

export const MobileOverlay = styled.button`
  -webkit-appearance: none;
  appearance: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(28, 28, 28, 0.3);
  z-index: 999;
  border: none;
  padding: 0;
  cursor: pointer;

  ${chatKeyboardFocusVisible}
`;

export const SettingsOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1001;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding: ${toRem(24)};

  @media ${baseVariables.mediaQuery.md} {
    align-items: flex-end;
    justify-content: flex-start;
    padding-left: ${toRem(100)};
    padding-bottom: ${toRem(80)};
  }
`;

export const SettingsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(16)};
  max-width: ${toRem(280)};
  width: 100%;
  animation: slideUp 0.2s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
